import { create } from "zustand"

export interface CommunityScript {
  id: string
  creator_id: string
  title: string
  description: string | null
  tags: string[]
  linked_content_id: string | null
  linked_content_site: string | null
  linked_content_url: string | null
  thumbnail_url: string | null
  preview_video_url: string | null
  content_type: string
  script_content: any
  script_duration_ms: number | null
  average_intensity: number
  price_cents: number
  download_price_cents: number | null
  trial_duration_seconds: number
  allow_download: boolean
  allow_purchaser_edit: boolean
  status: string
  is_featured: boolean
  view_count: number
  trial_count: number
  purchase_count: number
  total_revenue_cents: number
  avg_rating: number
  review_count: number
  intensity_samples: number[]
  created_at: string
  updated_at: string
  published_at: string | null
  // Joined fields
  creator_username?: string
  creator_avatar?: string
}

export interface ScriptAdCampaign {
  id: string
  creator_id: string
  script_id: string
  budget_cents: number
  spent_cents: number
  cost_per_impression_cents: number
  daily_budget_cents: number | null
  status: string
  target_tags: string[]
  target_devices: string[]
  start_date: string
  end_date: string | null
  impressions_shown: number
  clicks: number
  trials_started: number
  purchases_attributed: number
  revenue_attributed_cents: number
  ctr: number
  conversion_rate: number
  roi: number
  created_at: string
  updated_at: string
  // Joined fields
  script_title?: string
  script_thumbnail?: string
}

export interface ScriptPurchase {
  id: string
  script_id: string
  buyer_id: string
  price_paid_cents: number
  platform_fee_cents: number
  creator_revenue_cents: number
  status: string
  created_at: string
}

export interface ScriptReview {
  id: string
  script_id: string
  reviewer_id: string
  rating: number
  title: string | null
  review_text: string | null
  is_verified_purchase: boolean
  helpful_count: number
  created_at: string
  // Joined fields
  reviewer_username?: string
  reviewer_avatar?: string
}

export interface CreatorStats {
  total_revenue_cents: number
  total_sales: number
  total_scripts: number
  total_views: number
  total_trials: number
  avg_rating: number
  ad_spend_cents: number
  ad_impressions: number
  ad_clicks: number
  bounty_earnings_cents: number
  commission_earnings_cents: number
  artist_commission_earnings_cents: number
  goonpal_goop_earnings_cents: number
  support_earnings_cents: number
  game_earnings_cents: number
  widget_commission_earnings_cents: number
}

export interface SupportByStoryEntry {
  companionId: string
  companionName: string
  goop: number
  cents: number
  supporterCount: number
}

interface MarketplaceState {
  // Browsing
  scripts: CommunityScript[]
  featuredScripts: CommunityScript[]
  isLoading: boolean

  // User's purchases
  purchasedScripts: string[] // script IDs user has purchased

  // Creator's scripts
  myScripts: CommunityScript[]
  myCampaigns: ScriptAdCampaign[]

  // Signal to open a script modal (post-purchase or deep-link)
  pendingOpenScript: { scriptId: string; showPurchaseToast: boolean } | null
  setPendingOpenScript: (v: { scriptId: string; showPurchaseToast: boolean } | null) => void
  myStats: CreatorStats | null

  // Actions
  setScripts: (scripts: CommunityScript[]) => void
  setFeaturedScripts: (scripts: CommunityScript[]) => void
  setLoading: (loading: boolean) => void
  setPurchasedScripts: (ids: string[]) => void
  addPurchasedScript: (id: string) => void
  hasPurchased: (scriptId: string) => boolean
  setMyScripts: (scripts: CommunityScript[]) => void
  setMyCampaigns: (campaigns: ScriptAdCampaign[]) => void
  setMyStats: (stats: CreatorStats) => void
}

export const useMarketplaceStore = create<MarketplaceState>((set, get) => ({
  scripts: [],
  featuredScripts: [],
  isLoading: false,
  purchasedScripts: [],
  myScripts: [],
  myCampaigns: [],
  myStats: null,
  pendingOpenScript: null,

  setScripts: (scripts) => set({ scripts }),
  setFeaturedScripts: (scripts) => set({ featuredScripts: scripts }),
  setLoading: (loading) => set({ isLoading: loading }),
  setPurchasedScripts: (ids) => set({ purchasedScripts: ids }),
  addPurchasedScript: (id) =>
    set((state) => ({
      purchasedScripts: [...state.purchasedScripts, id],
    })),
  hasPurchased: (scriptId) => get().purchasedScripts.includes(scriptId),
  setMyScripts: (scripts) => set({ myScripts: scripts }),
  setMyCampaigns: (campaigns) => set({ myCampaigns: campaigns }),
  setMyStats: (stats) => set({ myStats: stats }),
  setPendingOpenScript: (v) => set({ pendingOpenScript: v }),
}))

// Pricing helpers
// Flat, aggressively low marketplace commission for everyone below Goon+ —
// the pitch is "2% flat, or 0% if you're a Patron of the app," beating every
// competitor (VGen 5% flat, Skeb ~5-10%, Fiverr 20%) at every tier rather
// than gating a bigger discount behind a subscription ladder. Legacy tiers
// (goontier_*) keep the old binary 0% treatment.
export const PLATFORM_FEE_PERCENTAGE: Record<string, number> = {
  free: 2,
  supporter: 2,
  goon: 2,
  goon_plus: 0,
  goon_max: 0,
  founder: 0,
  goontier_basic: 0,
  goontier_pro: 0,
  goontier_1: 0,
  goontier_2: 0,
}
// Back-compat exports — PAID no longer means a single flat rate, kept only
// for any external reference to "the 0% rate"; prefer getPlatformFeePercentage.
export const PLATFORM_FEE_PERCENTAGE_FREE = PLATFORM_FEE_PERCENTAGE.free
export const PLATFORM_FEE_PERCENTAGE_PAID = 0
export const MIN_SCRIPT_PRICE_CENTS = 99 // $0.99
export const MAX_SCRIPT_PRICE_CENTS = 4999 // $49.99

export const AD_TIERS = [
  { name: "Starter", budget_cents: 200, impressions: 100, description: "Test the waters" },
  { name: "Growth", budget_cents: 500, impressions: 500, description: "Recommended for most creators" },
  { name: "Pro", budget_cents: 2000, impressions: 2500, description: "Maximum visibility" },
]

export function getPlatformFeePercentage(creatorTier?: string | null): number {
  if (!creatorTier) return PLATFORM_FEE_PERCENTAGE.free
  return PLATFORM_FEE_PERCENTAGE[creatorTier] ?? PLATFORM_FEE_PERCENTAGE.free
}

export function calculateCreatorRevenue(priceCents: number, creatorTier?: string | null): number {
  const feePercentage = getPlatformFeePercentage(creatorTier)
  return Math.floor((priceCents * (100 - feePercentage)) / 100)
}

export function calculatePlatformFee(priceCents: number, creatorTier?: string | null): number {
  const feePercentage = getPlatformFeePercentage(creatorTier)
  return Math.floor((priceCents * feePercentage) / 100)
}

export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}
